import * as React from "react";
import {observer} from "mobx-react";
import styled, {css} from "styled-components";
import {withAppStores} from "../../../../AppStores";
import type {Xyicon} from "../../../../data/models/Xyicon";
import type {AppState} from "../../../../data/state/AppState";
import type {TransformObj} from "../../../../utils/dom/DomUtils";
import {DomUtils, HorizontalAlignment, VerticalAlignment} from "../../../../utils/dom/DomUtils";
import {AppUtils} from "../../../../utils/AppUtils";
import {KeyboardListener} from "../../../../utils/interaction/key/KeyboardListener";
import {FocusLoss} from "../../../../utils/ui/focus/FocusLoss";
import {XyiconFeature} from "../../../../generated/api/base";
import {Catalog} from "../../../../data/models/Catalog";
import {ReactUtils} from "../../../utils/ReactUtils";
import {SearchFieldV5} from "../../input/search/SearchFieldV5";
import {DomPortal} from "../../../modules/abstract/portal/DomPortal";
import {StringUtils} from "../../../../utils/data/string/StringUtils";
import {InfoBubbleV5} from "../../button/InfoBubbleV5";
import ChevronDownIcon from "../../icons/chevron-down.svg?react";
import {colorPalette} from "../../styles/colorPalette";
import {ConfirmXyiconModelChangesFormV5} from "../ConfirmXyiconModelChangesFormV5";
import {baseDistance, ELLIPSIS, FlexCenterStyle, fontSize, fontWeight, radius} from "../../styles/styles";
import {Spinner, SpinnerStyled} from "../../loader/Spinner";
import {Functions} from "../../../../utils/function/Functions";
import type {IModel} from "../../../../data/models/Model";
import {TextInputV5, TextInputWrapperStyled} from "./TextInputV5";

interface IXyiconModelFieldProps {
	readonly xyicons: Xyicon[];
	readonly item?: Xyicon;
	readonly appState?: AppState;
	readonly disabled: boolean;
	readonly inline?: boolean;
	readonly getItemsToUpdate?: () => IModel[];
}

interface IXyiconModelFieldState {
	isOpen: boolean;
	search: string;
	updating: boolean;
	xyiconsToBeChanged: Xyicon[];
	catalogIdToChangeTo: string;
	selectorTransform: TransformObj;
	selectorWidth: number;
	isToolTipOpen: boolean;
}

@withAppStores("appState")
@observer
export class XyiconModelFieldV5 extends React.Component<IXyiconModelFieldProps, IXyiconModelFieldState> {
	private _container = React.createRef<HTMLDivElement>();
	private _selector = React.createRef<HTMLDivElement>();
	private _timeoutId: number;

	constructor(props: IXyiconModelFieldProps) {
		super(props);
		this.state = {
			isOpen: false,
			search: "",
			updating: false,
			xyiconsToBeChanged: [],
			catalogIdToChangeTo: "",
			selectorTransform: null,
			selectorWidth: 300,
			isToolTipOpen: false,
		};
	}

	private onEditClick = () => {
		if (this.props.disabled) {
			return;
		}

		const isOpen = !this.state.isOpen;
		this.setState({
			isOpen,
			search: "",
			// Render at the anchor width before measuring the floating position.
			selectorWidth: isOpen ? this._container.current.getBoundingClientRect().width : this.state.selectorWidth,
		});
	};

	private onKeyPressed = (event: KeyboardEvent) => {
		switch (event.key) {
			case KeyboardListener.KEY_ESCAPE:
				if (this.state.isOpen) {
					this.setState({isOpen: false, search: ""});
				}
				break;
		}
	};

	private onSelectCatalog = (catalogId: string) => {
		const {xyicons, inline, item, getItemsToUpdate} = this.props;
		// for grids, getItemsToUpdate returns all the selected items to change model. doing this here for performance reasons.
		const items = (getItemsToUpdate?.() as Xyicon[]) ?? xyicons;

		// in case of SpaceEditor table xyicon model change (xyicons is empty) or if some items are selected and other element's model is changed
		const xyiconsToCheck = inline && (items.length === 0 || !items.includes(item)) ? [item] : items;
		const xyiconsToBeChanged = xyiconsToCheck.filter((xyicon) => xyicon.catalogId !== catalogId);

		this.setState({
			xyiconsToBeChanged: xyiconsToBeChanged,
			catalogIdToChangeTo: catalogId,
		});
	};

	private areAllModelsMatching() {
		const {xyicons} = this.props;
		const firstCatalogId = xyicons[0]?.catalogId;

		for (let i = 1; i < xyicons.length; ++i) {
			if (xyicons[i].catalogId !== firstCatalogId) {
				return false;
			}
		}

		return true;
	}

	private onFocusLoss = (e: MouseEvent) => {
		let eventTargetInModalContainer = false;

		if (e.target instanceof Element) {
			eventTargetInModalContainer = this.props.appState.app.modalContainer.contains(e.target);

			if (!eventTargetInModalContainer && (this.state.isOpen || this.state.search)) {
				this.setState({
					isOpen: false,
					search: "",
				});
			}
		}

		return false;
	};

	private onSearchInput = (value: string) => {
		this.setState({
			search: value,
		});
	};

	private onCloseConfirmForm = () => {
		this.setState({
			xyiconsToBeChanged: [],
			catalogIdToChangeTo: "",
			isOpen: false,
			search: "",
		});
	};

	private onMouseOver = (e: React.MouseEvent) => {
		if (this.props.inline) {
			return;
		}
		const input = e.currentTarget.querySelector("input") as HTMLInputElement;

		if (input && input.scrollWidth > input.clientWidth) {
			if (this._timeoutId) {
				clearTimeout(this._timeoutId);
			}

			this._timeoutId = window.setTimeout(() => {
				this.setState({isToolTipOpen: true});
			}, 500);
		}
	};

	private onMouseLeave = () => {
		clearTimeout(this._timeoutId);
		if (this.state.isToolTipOpen) {
			this.setState({isToolTipOpen: false});
		}
	};

	private startEditingListeners() {
		FocusLoss.listen(this._container.current, this.onFocusLoss);
		KeyboardListener.getInstance().signals.up.add(this.onKeyPressed);
		AppUtils.disableScrolling(true);
	}

	private stopEditingListeners() {
		FocusLoss.stopListen(this._container.current, this.onFocusLoss);
		KeyboardListener.getInstance().signals.up.remove(this.onKeyPressed);
		AppUtils.disableScrolling(false);
	}

	public override componentWillUnmount() {
		clearTimeout(this._timeoutId);
		if (this.state.isOpen) {
			this.stopEditingListeners();
		}
	}

	public override componentDidUpdate(prevProps: Readonly<IXyiconModelFieldProps>, prevState: Readonly<IXyiconModelFieldState>): void {
		if (prevState.isOpen && !this.state.isOpen) {
			this.stopEditingListeners();
		} else if (!prevState.isOpen && this.state.isOpen && this._container.current && this._selector.current) {
			this.startEditingListeners();
			this.setState({
				selectorTransform: DomUtils.getFixedFloatingElementPosition(
					this._container.current,
					this._selector.current,
					VerticalAlignment.bottomOuter,
					HorizontalAlignment.center,
					3,
					0,
				),
			});
		}
	}

	public override render() {
		const {xyicons, appState, disabled, inline, item} = this.props;
		const {isOpen, search, updating, isToolTipOpen, selectorTransform, selectorWidth} = this.state;
		const hasPendingModelChange = this.state.xyiconsToBeChanged.length > 0 && !!this.state.catalogIdToChangeTo;

		const selectorStyle: React.CSSProperties = isOpen && {
			transform: selectorTransform?.translate,
			position: "fixed",
			zIndex: 9000,
			minWidth: 300,
			width: selectorWidth,
			maxWidth: 500,
		};

		// A mounted grid can contain hundreds of model fields. The closed label only
		// needs the selected catalog; enumerate the other catalogs when editing.
		let catalogs = isOpen ? (appState.actions.getList(XyiconFeature.XyiconCatalog) as Catalog[]) : [];
		let matchingCatalog: Catalog;

		if (inline || this.areAllModelsMatching()) {
			const firstXyicon = inline ? item : xyicons[0];

			if (firstXyicon) {
				if (isOpen) {
					catalogs = catalogs.filter((catalog) => {
						return catalog.id !== firstXyicon.catalogId && Catalog.search(catalog, search);
					});
				}
				matchingCatalog = appState.actions.findInheritedObject(firstXyicon, firstXyicon.ownFeature) as Catalog;
			}
		}

		return (
			<XyiconModelFieldStyled
				ref={this._container}
				className={ReactUtils.cls("XyiconModelField", {isOpen, inline, disabled})}
				onMouseOver={this.onMouseOver}
				onMouseLeave={this.onMouseLeave}
				$multipleValues={!matchingCatalog}
			>
				<div
					className={ReactUtils.cls("modelField", {disabled})}
					onClick={this.onEditClick}
				>
					<TextInputV5
						onClick={Functions.preventDefault}
						value={matchingCatalog ? matchingCatalog.model : "Multiple values"}
						onKeyDown={Functions.preventDefault}
						className={ReactUtils.cls("XyiconModelField", {cellContent: inline})}
						disabled={disabled}
						readOnly={true}
					/>
					{updating ? <Spinner isVisible={true} /> : !disabled && !inline && xyicons.length === 1 && <ChevronDownIcon />}
				</div>
				{hasPendingModelChange && (
					<ConfirmXyiconModelChangesFormV5
						onClose={this.onCloseConfirmForm}
						xyiconsToBeChanged={this.state.xyiconsToBeChanged}
						catalogIdToChangeTo={this.state.catalogIdToChangeTo}
					/>
				)}
				{isOpen && (
					<DomPortal destination={this.props.appState.app.modalContainer}>
						<XyiconModelFieldSelectorStyled
							className={ReactUtils.cls("XyiconModelField__selector", {inline})}
							style={selectorStyle}
							ref={this._selector}
						>
							<SearchFieldV5
								onInput={this.onSearchInput}
								value={search}
							/>
							<div className="list">
								{catalogs.length ? (
									catalogs
										.toSorted((a: Catalog, b: Catalog) => StringUtils.sortIgnoreCase(a.model, b.model))
										.filter((c: Catalog) => StringUtils.containsIgnoreCase(c.model, search))
										.map((catalog) => (
											<div
												key={catalog.id}
												className="listItem"
												onClick={(e) => this.onSelectCatalog(catalog.id)}
											>
												<img
													src={catalog.thumbnail}
													alt={`${catalog.id} thumbnail`}
												/>
												<div className="fields">
													{search.length !== 0 ? (
														<div
															className="model"
															dangerouslySetInnerHTML={{__html: StringUtils.regexHighlight(catalog.model, search)}}
														/>
													) : (
														<div className="model">{catalog.model}</div>
													)}
													<div className="type">{appState.actions.getTypeName(catalog.typeId)}</div>
												</div>
											</div>
										))
								) : (
									<>No results.</>
								)}
							</div>
						</XyiconModelFieldSelectorStyled>
					</DomPortal>
				)}
				{isToolTipOpen && matchingCatalog && !inline && (
					<InfoBubbleV5
						content={matchingCatalog.model}
						anchorRef={this._container}
						positionOffsetY={10}
						className="DeleteButtonToolTip"
					/>
				)}
			</XyiconModelFieldStyled>
		);
	}
}

export const XyiconModelFieldStyled = styled.div<{$multipleValues?: boolean}>`
	cursor: pointer;
	padding-right: ${baseDistance.xs};

	&:hover {
		background-color: ${colorPalette.gray.c200Light};
	}

	&.disabled {
		cursor: default;

		&:hover {
			background-color: transparent;
		}
	}

	.modelField {
		& > ${TextInputWrapperStyled} > input {
			caret-color: transparent;
			cursor: pointer;
		}

		&.disabled {
			cursor: default;

			& > ${TextInputWrapperStyled} > input {
				cursor: default;
			}
		}
	}

	.cellContent {
		background: none;

		&:hover {
			background: var(--inlineEditHover);
		}
	}

	&:not(.inline) {
		position: relative;
		width: 100%;
		left: -12px;

		.modelField {
			${FlexCenterStyle};

			&.disabled {
				outline: none;
			}

			input {
				pointer-events: none;
				border: none;
				flex: 1;

				${(props) => {
					if (props.$multipleValues) {
						return css`
							font-style: italic;
						`;
					}
				}}

				& + svg {
					margin-right: ${baseDistance.sm};
				}
			}

			svg.icon {
				display: none;
				position: absolute;
				width: 16px;
				height: 16px;
				top: 50%;
				transform: translateY(-50%);
				right: 11px;
				fill: var(--icon);
			}

			${SpinnerStyled} {
				position: absolute;
				top: 11px;
				right: base.$xs;
				visibility: visible;
			}
		}

		> input {
			border: solid 1px transparent;
			font-weight: base.$font-thin;
			cursor: pointer;
		}

		&:hover {
			> input {
				border-color: var(--bg3);
			}
		}
	}

	&.isOpen {
		> input {
			background: var(--bg3);
			border-radius: ${radius.sm};
		}

		> svg.icon {
			display: block;
		}

		.SearchField {
			svg.icon {
				display: block;
			}
		}
	}
`;

const XyiconModelFieldSelectorStyled = styled.div`
	background-color: ${colorPalette.white};
	padding: ${baseDistance.sm};
	box-shadow:
		0px 8px 16px 0px rgba(50, 50, 71, 0.06),
		0px 8px 8px 0px rgba(50, 50, 71, 0.08);
	border-radius: ${radius.sm};

	.list {
		overflow-y: auto;
		background: ${colorPalette.white};
		max-height: 300px;
		overflow-x: hidden;
	}

	.listItem {
		display: grid;
		grid-template-columns: 38px 1fr;
		grid-gap: base.$sm;
		padding: ${baseDistance.sm};
		height: auto;
		border-color: transparent;
		width: 100%;

		&:hover {
			background-color: ${colorPalette.gray.c200Light};
			border-radius: ${radius.sm};
		}

		.fields {
			min-width: 100%;
			padding-left: ${baseDistance.sm};
			line-height: 16px;
			font-weight: ${fontWeight.normal};

			& > div {
				${ELLIPSIS};
			}
		}

		img {
			width: 38px;
			height: 38px;
		}

		.model {
			font-size: ${fontSize.md};
			color: ${colorPalette.gray.c950};
		}

		.type {
			font-size: ${fontSize.sm};
			letter-spacing: 0.0025em;
			color: ${colorPalette.gray.c700Dark};
		}

		&.hover {
			.model {
				color: ${colorPalette.white};
			}

			.email {
				color: ${colorPalette.primary.c200Light};
			}
		}
	}

	.SearchField {
		height: auto;
		margin: 0 0 2px;
		width: 100%;

		input {
			border-radius: ${radius.sm};
		}

		&:focus,
		&:active,
		&:hover {
			input {
				border-color: ${colorPalette.primary.c500Primary};
			}
		}
	}
`;
